// =============================================================================
// FILE: packages/renderer/src/components/branding/AnthemPlayer.tsx
// PURPOSE: Plays "The RCD² Engine Anthem" once when the operator enters the
//          dashboard (first mount of the authenticated shell per browser
//          session). Respects the global mute preference. If the browser blocks
//          autoplay, a small "Play Anthem" pill lets the user start it. A stop
//          control is shown while it plays.
// =============================================================================

import { useEffect, useRef, useState, type JSX } from 'react';
import { Music, Play, X } from 'lucide-react';
import { isSoundEnabled } from '../../lib/soundManager.js';

const ANTHEM_SRC = `${import.meta.env.BASE_URL}rcd2-anthem.mp3`;
const SESSION_KEY = 'rcd2.anthem.played';
const DISABLED_KEY = 'rcd2.anthem.disabled';

function isAnthemDisabled(): boolean {
  try { return localStorage.getItem(DISABLED_KEY) === '1'; } catch { return false; }
}

export function AnthemPlayer(): JSX.Element | null {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [status, setStatus] = useState<'idle' | 'playing' | 'blocked' | 'done'>('idle');

  useEffect(() => {
    if (isAnthemDisabled()) { setStatus('done'); return; }
    try { if (sessionStorage.getItem(SESSION_KEY)) { setStatus('done'); return; } } catch { /* */ }
    if (!isSoundEnabled()) { setStatus('blocked'); return; }  // muted — offer a manual play pill
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0.6;
    a.play()
      .then(() => { setStatus('playing'); try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* */ } })
      .catch(() => setStatus('blocked'));  // autoplay blocked — offer manual start
  }, []);

  const start = () => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0.6;
    void a.play().then(() => { setStatus('playing'); try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* */ } }).catch(() => setStatus('blocked'));
  };

  const stop = () => {
    const a = audioRef.current;
    if (a) { a.pause(); a.currentTime = 0; }
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* */ }
    setStatus('done');
  };

  const disableForever = () => {
    try { localStorage.setItem(DISABLED_KEY, '1'); } catch { /* */ }
    stop();
  };

  return (
    <>
      <audio ref={audioRef} src={ANTHEM_SRC} preload="auto" onEnded={() => setStatus('done')} data-testid="anthem-audio" />
      {status === 'playing' && (
        <div
          data-testid="anthem-now-playing"
          style={{
            position: 'fixed', left: 20, bottom: 20, zIndex: 9000,
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', borderRadius: 999,
            background: 'rgba(6,14,24,0.92)', border: '1px solid rgba(45,212,191,0.35)',
            boxShadow: '0 8px 28px rgba(0,0,0,0.5), 0 0 22px rgba(45,212,191,0.14)',
            backdropFilter: 'blur(12px)', animation: 'gc-slide-in 0.35s cubic-bezier(0.34,1.56,0.64,1)',
          }}
        >
          <Music className="animate-pulse" style={{ width: 13, height: 13, color: '#5eead4' }} />
          <span style={{ fontSize: 10.5, letterSpacing: '0.06em', color: 'rgba(210,235,230,0.9)' }}>RCD² Engine Anthem</span>
          <button type="button" data-testid="anthem-disable" data-nosound onClick={disableForever} title="Don't play the anthem on startup anymore"
            style={{ marginLeft: 4, padding: '2px 7px', borderRadius: 999, background: 'transparent', border: '1px solid rgba(148,163,184,0.3)', color: 'rgba(180,200,210,0.7)', cursor: 'pointer', fontSize: 8.5, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
            Don't play next time
          </button>
          <button type="button" data-testid="anthem-stop" data-nosound onClick={stop} title="Stop anthem"
            style={{ width: 18, height: 18, display: 'grid', placeItems: 'center', borderRadius: 5, background: 'transparent', border: '1px solid rgba(148,163,184,0.35)', color: 'rgba(180,200,210,0.8)', cursor: 'pointer' }}>
            <X style={{ width: 10, height: 10 }} />
          </button>
        </div>
      )}
      {status === 'blocked' && (
        <button
          type="button"
          data-testid="anthem-play"
          data-nosound
          onClick={start}
          title="Play the RCD² Engine Anthem"
          style={{
            position: 'fixed', left: 20, bottom: 20, zIndex: 9000,
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '9px 14px', borderRadius: 999,
            background: 'rgba(6,14,24,0.92)', border: '1px solid rgba(45,212,191,0.45)',
            boxShadow: '0 8px 28px rgba(0,0,0,0.5), 0 0 22px rgba(45,212,191,0.2)',
            backdropFilter: 'blur(12px)', color: '#5eead4', cursor: 'pointer',
            fontSize: 11, letterSpacing: '0.06em',
          }}
        >
          <Play style={{ width: 12, height: 12 }} fill="currentColor" />
          Play Anthem
        </button>
      )}
    </>
  );
}
